import {
  faRoute,
  faUser,
  faFolderUser,
  faUserGear,
  faUserShield,
  faGrid2Plus,
  faChartLine,
  faSitemap,
  faEnvelope,
} from "@fortawesome/pro-light-svg-icons";
import { t } from "i18next";
import adminHelper from "../../helpers/adminHelper";
import { UserContextInterface } from "../../state/UserContext";
import SubNavItem from "../../types/nav/SubNavItem";
import AppRoutes from "../AppRoutes";
import permissionHelper from "../../helpers/permissionHelper";
import selfServeNotificationsAccessHelper from "../../helpers/selfServeNotificationsAccessHelper";

const AdminSubNavItems = (
  userContext: UserContextInterface,
  navType: "LEFT-NAV" | "TOUCH",
): SubNavItem[] => {
  const translationIdentifierPrefix = `Nav.${
    navType === "LEFT-NAV" ? "LargeScreen" : "Touch"
  }.Admin.`;

  const output: SubNavItem[] = [];

  // 'Dashboards' Item
  if (
    permissionHelper.userHasEitherPermission(
      userContext,
      "ManageContentDashboards",
      "MANAGE-CONTENT-DASHBOARDS",
    )
  ) {
    output.push({
      title: translationIdentifierPrefix + "Dashboards",
      translateTitle: true,
      url: AppRoutes.admin.dashboards.path,
      icon: faGrid2Plus,
    });
  }

  // 'Journeys' Item
  // Check if the user has access to the 'JOURNEY-CONFIG' module
  if (
    adminHelper.doesUserHaveAdminModuleAccess(userContext, "JOURNEY-CONFIG")
  ) {
    output.push({
      title: translationIdentifierPrefix + "Journeys",
      translateTitle: true,
      url: AppRoutes.admin.journeyManagement.path,
      icon: faRoute,
    });
  }

  // 'Roles' Item
  // Check if the user has access to the 'JOURNEY-CONFIG' module
  if (permissionHelper.userHasPermission(userContext, "MANAGE-ROLES")) {
    output.push({
      title: translationIdentifierPrefix + "Roles",
      translateTitle: true,
      url: AppRoutes.admin.roles.path,
      icon: faUserShield,
    });
  }

  // 'Users' Item — align with Admin dashboard "User Management" card: USER-JOURNEY module
  // plus employee-management permission, or non-admin manager with ManagersAdministerTheirEmployees.
  const hasUserJourneyModuleAccess = adminHelper.doesUserHaveAdminModuleAccess(
    userContext,
    "USER-JOURNEY",
  );

  const managerAdminsTheirPeopleConfig =
    userContext.user.client.moduleConfigs.find((config) => {
      return config.key === "ManagersAdministerTheirEmployees";
    });
  const isNonAdminUserWithManagerConfigAccess =
    !userContext.user.isAdmin &&
    managerAdminsTheirPeopleConfig != undefined &&
    managerAdminsTheirPeopleConfig.value === "true" &&
    userContext.user.isManager;

  const hasUserManagementPermission = permissionHelper.userHasEitherPermission(
    userContext,
    "ViewAllEmployees",
    "MANAGE-EMPLOYEES",
  );

  if (
    (hasUserJourneyModuleAccess && hasUserManagementPermission) ||
    isNonAdminUserWithManagerConfigAccess
  ) {
    output.push({
      title: translationIdentifierPrefix + "UserManagement",
      translateTitle: true,
      url: AppRoutes.admin.userManagement.path,
      icon: faUser,
    });
  }

  // 'User Groups' Item
  // Check if the user has the 'ManageUserGroups'or 'ViewUserGroups' permission
  if (
    permissionHelper.userHasEitherPermission(
      userContext,
      "ManageUserGroups",
      "MANAGE-USER-GROUPS",
    ) ||
    permissionHelper.userHasEitherPermission(
      userContext,
      "ViewUserGroups",
      "VIEW-USER-GROUPS",
    )
  ) {
    output.push({
      title: translationIdentifierPrefix + "UserGroups",
      translateTitle: true,
      url: AppRoutes.admin.userGroups.root,
      icon: faFolderUser,
    });
  }

  // 'Notifications' Item
  if (
    selfServeNotificationsAccessHelper.userCanAccessSelfServeNotifications(
      userContext,
    )
  ) {
    output.push({
      title: "Notifications",
      translateTitle: false,
      url: AppRoutes.admin.notifications.root,
      icon: faEnvelope,
    });
  }

  // 'Brands' Item - Desktop only (LEFT-NAV)
  if (
    navType === "LEFT-NAV" &&
    permissionHelper.userHasEitherPermission(
      userContext,
      "ManageBrands",
      "MANAGE-BRANDS"
    )
  ) {
    output.push({
      title: translationIdentifierPrefix + "Brands",
      translateTitle: true,
      url: AppRoutes.admin.brands.path,
      icon: faSitemap,
    });
  }

  // Legacy User Management
  if (
    !userContext.user.client.hasValidClientApi &&
    (hasUserJourneyModuleAccess || isNonAdminUserWithManagerConfigAccess)
  ) {
    output.push({
      title: "User Management",
      translateTitle: false,
      url: AppRoutes.admin.oldSystemUserManagement,
      icon: faUserGear,
    });
  }

  // 'DataPoints' Item
  const hasDataPointsPermission =
    userContext.user.client.permissionMode === "LEGACY-PERMISSIONS"
      ? permissionHelper.userHasPermission(userContext, "ConfigureAnalytics")
      : permissionHelper.userHasPermission(userContext, "CONFIGURE-ANALYTICS");
  if (hasDataPointsPermission) {
    output.push({
      title: translationIdentifierPrefix + "Analytics",
      translateTitle: true,
      url: AppRoutes.admin.datapoints.root,
      icon: faChartLine,
    });
  }

  const adminSubNavSortLabel = (item: SubNavItem): string =>
    (item.translateTitle ? t(item.title) : item.title).toLocaleLowerCase();

  return output.sort((a, b) =>
    adminSubNavSortLabel(a).localeCompare(adminSubNavSortLabel(b)),
  );
};

export default AdminSubNavItems;
